import Script from 'next/script'
import { useTranslation } from 'next-i18next'
import { type BreadCrumbJsonLdProps } from 'next-seo'
import { type FC, type ReactNode, useEffect, useState } from 'react'
import { useTheme } from 'styled-components'

import { getSrpBreadcrumbs } from '@/domain/breadcrumbs'
import { getSearchCategoryFromSearchQuery } from '@/domain/category/getSearchCategoryFromSearchQuery'
import { useGetSearchResultsData } from '@/hooks/srp/useGetSearchResultsData'
import { useLocale } from '@/hooks/useLocale'
import { Skeleton } from '@/ui/atoms/skeleton'
import { Spacing } from '@/ui/atoms/spacing'
import {
  type BreadcrumbsProps as UIBreadcrumbsProps,
  Breadcrumbs as UIBreadcrumbs,
} from '@/ui/molecules/breadcrumbs'
import { replaceAmpersands } from '@/utils/string'

const JSON_LD_BREADCRUMBS_ID = 'srp-breadcrumbs-json-ld'

type JsonLdProps = {
  pageLoadId: string
  breadcrumbs?: UIBreadcrumbsProps['crumbs']
}

type BreadcrumbsJsonLd = {
  '@context': 'https://schema.org'
  '@type': 'BreadcrumbList'
  itemListElement: BreadCrumbJsonLdProps['itemListElements']
}

type BreadcrumbsProps = {
  pageLoadId?: string
}

export const Breadcrumbs: FC<BreadcrumbsProps> = ({ pageLoadId }) => {
  const { routeLocale } = useLocale()
  const { spacing } = useTheme()
  const { t } = useTranslation('srp')

  /**
   * It should get the cache saved on the server-side
   * This query access the same main call from the SRP requesting just the fragment needed for the breadcrumbs
   * */
  const { data: { searchQuery } = {}, loading } = useGetSearchResultsData({
    fetchPolicy: 'cache-only',
  })

  const formatCrumbsJsonLd = (
    crumbs: NonNullable<JsonLdProps['breadcrumbs']>
  ): BreadcrumbsJsonLd => {
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: crumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: { '@id': crumb.href || '', name: replaceAmpersands([crumb.label])[0] },
      })),
    }
  }

  const getCrumbsScriptComponent = (
    pageLoadId: BreadcrumbsProps['pageLoadId'],
    crumbs: JsonLdProps['breadcrumbs']
  ): ReactNode => {
    if (!crumbs || !pageLoadId) return null

    const crumbJSON = formatCrumbsJsonLd(crumbs)

    return (
      <Script
        key={`${pageLoadId}-${JSON_LD_BREADCRUMBS_ID}`}
        id={JSON_LD_BREADCRUMBS_ID}
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(crumbJSON),
        }}
      />
    )
  }

  const category = getSearchCategoryFromSearchQuery(searchQuery?.category)

  /** Breadcrumbs */
  const breadcrumbs = getSrpBreadcrumbs({
    activeCategory: category,
    categoryPath: category.path || [],
    keywords: searchQuery?.keywords,
    routeLocale,
  })

  const [jsonLdScript] = useState<ReactNode>(getCrumbsScriptComponent(pageLoadId, breadcrumbs))

  /** Update breadcrumbs JSON-LD on breadcrumbs change */
  useEffect(() => {
    const breadcrumbJSON = formatCrumbsJsonLd(breadcrumbs)
    const script = document.getElementById(JSON_LD_BREADCRUMBS_ID)

    if (script) {
      script.innerHTML = JSON.stringify(breadcrumbJSON)
      return
    }
  }, [breadcrumbs])

  if (loading || !searchQuery) return <Skeleton width="30%" />

  /** The component shouldn't take the top space if it is empty */
  if (!breadcrumbs?.length) return null

  return (
    <>
      {jsonLdScript}

      <Spacing mBottom={spacing.defaultSmall} data-testid="search-breadcrumbs">
        <UIBreadcrumbs crumbs={breadcrumbs} label={t('breadcrumb.label')} />
      </Spacing>
    </>
  )
}
